import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { BatteryChargingFullOutlined, ElectricalServicesOutlined, ElectricBoltOutlined, SwapVert } from '@mui/icons-material';
import PowerGauge from './PowerGauge';
import NumberLabel from './NumberLabel';
import HourlyPlot from './HourlyPlot';
import DailyPlot from './DailyPlot';
import PlotModeToggle from '../PlotModeToggle';

const PcsCard = ({
  id,
  label,
  data,
}) => {

  const [tsHourlyCharge, setTsHourlyCharge] = useState(null);
  const [tsHourlyDischg, setTsHourlyDischg] = useState(null);
  const [tsHourlyTemp, setTsHourlyTemp] = useState(null);

  const [tsDailyCharge, setTsDailyCharge] = useState(null);
  const [tsDailyDischg, setTsDailyDischg] = useState(null);
  const [tsDailyTempMin, setTsDailyTempMin] = useState(null);
  const [tsDailyTempMax, setTsDailyTempMax] = useState(null);

  const [plotMode, setPlotMode] = useState("hourly");

  const processHourlyData = (data) => {
    let _tsHourlyCharge = {
      x: [],
      y: [],
      name: "Charge",
      type: "bar",
      // Outline in the bar colour so zero values still render as a thin line
      marker: { color: "#3B82F6", line: { color: "#3B82F6", width: 1 } },
    };
    let _tsHourlyDischg = {
      x: [],
      y: [],
      name: "Discharge",
      type: "bar",
      // Outline in the bar colour so zero values still render as a thin line
      marker: { color: "#142784", line: { color: "#142784", width: 1 } },
    };
    let _tsHourlyTemp = {
      x: [],
      y: [],
      name: "Temperature",
      line: { color: "#F97316", width: 2.5 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };

    Object.entries(data).map(([epoch, epochData]) => {
      // console.log(epochData);
      if (epochData.local_datetime_str) {
        if (epochData.E_AC_charge != null) {
          _tsHourlyCharge.x.push(epochData.local_datetime_str);
          _tsHourlyCharge.y.push(epochData.E_AC_charge);
        }
        if (epochData.E_AC_discharge != null) {
          _tsHourlyDischg.x.push(epochData.local_datetime_str);
          _tsHourlyDischg.y.push(epochData.E_AC_discharge);
        }
        if (epochData.T_ambient_max != null) {
          _tsHourlyTemp.x.push(epochData.local_datetime_str);
          _tsHourlyTemp.y.push(epochData.T_ambient_max);
        }
      }
    });

    setTsHourlyCharge(_tsHourlyCharge);
    setTsHourlyDischg(_tsHourlyDischg);
    setTsHourlyTemp(_tsHourlyTemp);
  }

  const processDailyData = (data) => {
    let _tsDailyCharge = {
      x: [],
      y: [],
      name: "Charge",
      type: "bar",
      // Outline in the bar colour so zero values still render as a thin line
      marker: { color: "#3B82F6", line: { color: "#3B82F6", width: 1 } },
    };
    let _tsDailyDischg = {
      x: [],
      y: [],
      name: "Discharge",
      type: "bar",
      // Outline in the bar colour so zero values still render as a thin line
      marker: { color: "#142784", line: { color: "#142784", width: 1 } },
    };
    let _tsDailyTempMin = {
      x: [],
      y: [],
      name: "Min temp.",
      line: { color: "#0EA5E9", width: 2 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };
    let _tsDailyTempMax = {
      x: [],
      y: [],
      name: "Max temp.",
      line: { color: "#EF4444", width: 2 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };


    Object.entries(data).map(([epoch, epochData]) => {
      // console.log(epochData);
      if (epochData.local_datetime_str) {
        if (epochData.E_AC_charge != null) {
          _tsDailyCharge.x.push(epochData.local_datetime_str);
          _tsDailyCharge.y.push(epochData.E_AC_charge);
        }
        if (epochData.E_AC_discharge != null) {
          _tsDailyDischg.x.push(epochData.local_datetime_str);
          _tsDailyDischg.y.push(epochData.E_AC_discharge);
        }
        if (epochData.T_ambient_min != null) {
          _tsDailyTempMin.x.push(epochData.local_datetime_str);
          _tsDailyTempMin.y.push(epochData.T_ambient_min);
        }
        if (epochData.T_ambient_max != null) {
          _tsDailyTempMax.x.push(epochData.local_datetime_str);
          _tsDailyTempMax.y.push(epochData.T_ambient_max);
        }
      }
    });

    setTsDailyCharge(_tsDailyCharge);
    setTsDailyDischg(_tsDailyDischg);
    setTsDailyTempMin(_tsDailyTempMin);
    setTsDailyTempMax(_tsDailyTempMax);
  }

  useEffect(() => {
    if (data != null && data.pcs_data_ts_hour != null) {
      processHourlyData(data.pcs_data_ts_hour);

    } else {
      setTsHourlyCharge(null);
      setTsHourlyDischg(null);
      setTsHourlyTemp(null);
    }

    if (data != null && data.pcs_data_ts_day != null) {
      processDailyData(data.pcs_data_ts_day);

    } else {
      setTsDailyCharge(null);
      setTsDailyDischg(null);
      setTsDailyTempMin(null);
      setTsDailyTempMax(null);
    }
  }, [data]);

  const latest = data.pcs_data_latest || {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* Unit label */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 2.5, pt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--c-text-2)" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "var(--c-text-faint)", fontFamily: "monospace" }} title={id}>
          {id.slice(0, 8).toUpperCase()}
        </Typography>
      </Box>

      {/* Graphics box */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
        alignItems: "center",
        gap: 3,
        p: 2.5,
      }}>
        {/* Power gauge */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {latest.P_active_kW != null &&
            <PowerGauge
              power={latest.P_active_kW}
              powerPercent={Math.abs(latest.P_active_percent)}
              statusText={latest.state}
            />
          }
        </Box>

        {/* Plot */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--c-text-muted)" }}>
              {plotMode === "hourly" ? "AC energy and temperature, hourly" : "AC energy and temperature, daily"}
            </Typography>
            <PlotModeToggle value={plotMode} onChange={setPlotMode} />
          </Box>

          <Box sx={{ height: 220 }}>
            {plotMode === "hourly"
              && tsHourlyCharge != null
              && tsHourlyDischg != null
              && tsHourlyTemp != null
              &&
              <HourlyPlot
                tsCharge={tsHourlyCharge}
                tsDischarge={tsHourlyDischg}
                tsTemperature={tsHourlyTemp}
              />
            }

            {plotMode === "daily"
              && tsDailyCharge != null
              && tsDailyDischg != null
              && tsDailyTempMin != null
              && tsDailyTempMax != null
              &&
              <DailyPlot
                tsCharge={tsDailyCharge}
                tsDischarge={tsDailyDischg}
                tsTemperatureMin={tsDailyTempMin}
                tsTemperatureMax={tsDailyTempMax}
              />
            }
          </Box>
        </Box>
      </Box>

      {/* Labels box */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
        gap: 1.5,
        px: 2.5,
        pb: 2.5,
      }}>
        {latest.V_DC != null &&
          <NumberLabel
            label="DC battery voltage"
            icon={<BatteryChargingFullOutlined />}
            value={latest.V_DC}
            unit="V"
          />
        }
        {latest.I_DC != null &&
          <NumberLabel
            label="DC battery current"
            icon={<SwapVert />}
            value={latest.I_DC}
            unit="A"
          />
        }
        {latest.V_AC_output != null &&
          <NumberLabel
            label="AC output voltage"
            icon={<ElectricalServicesOutlined />}
            value={latest.V_AC_output}
            unit="V"
          />
        }
        {latest.I_AC_output != null &&
          <NumberLabel
            label="AC output current"
            icon={<ElectricBoltOutlined />}
            value={latest.I_AC_output}
            unit="A"
          />
        }
      </Box>
    </Box>
  )
}

export default PcsCard
